import React from 'react'
import { Box, Grid, Paper, Typography, makeStyles } from '@material-ui/core'
import AssessmentIcon from '@material-ui/icons/Assessment'
import PeopleIcon from '@material-ui/icons/People'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import { toFarsiDigits } from '../../assets/utils'

const useStyles = makeStyles(() => ({
  card: {
    position: 'relative',
    overflow: 'hidden',
    padding: '16px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    borderRadius: 16,
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0 6px 18px rgba(20,30,80,.16)',
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 12px 28px rgba(20,30,80,.26)',
    },
  },
  active: {
    outline: '3px solid rgba(255,255,255,.85)',
    outlineOffset: '-3px',
    transform: 'translateY(-3px)',
  },
  blob: {
    position: 'absolute',
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: 'rgba(255,255,255,.16)',
    top: -46,
    left: -26,
  },
  blob2: {
    position: 'absolute',
    width: 70,
    height: 70,
    borderRadius: '50%',
    background: 'rgba(255,255,255,.12)',
    bottom: -30,
    right: 18,
  },
  iconBox: {
    position: 'relative',
    width: 46,
    height: 46,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,.22)',
    flexShrink: 0,
  },
  value: {
    position: 'relative',
    fontSize: 27,
    fontWeight: 900,
    lineHeight: 1.25,
    textShadow: '0 1px 3px rgba(0,0,0,.18)',
  },
  value2: {
    position: 'relative',
    fontSize: 27,
    fontWeight: 900,
    lineHeight: 1.25,
    textShadow: '0 1px 3px rgba(0,0,0,.18)',
  },
  unit: {
    position: 'relative',
    fontSize: 13,
    fontWeight: 700,
    opacity: .95,
    textShadow: '0 1px 2px rgba(0,0,0,.14)',
  },
  label: {
    position: 'relative',
    fontSize: 13,
    fontWeight: 700,
    opacity: .95,
    textShadow: '0 1px 2px rgba(0,0,0,.14)',
  },
  hint: {
    position: 'relative',
    fontSize: 10.5,
    opacity: .8,
    marginTop: 2,
  },
}))

const CARDS = [
  {
    key: 'all',
    label: 'تعداد قرارداد',
    hint: 'کلِ قراردادها',
    bg: 'linear-gradient(135deg, #5b6ef5 0%, #8f7bff 100%)',
    icon: <AssessmentIcon />,
  },
  {
    key: 'contractors',
    label: 'تعداد پیمانکار',
    hint: 'پیمانکارانِ یکتا',
    bg: 'linear-gradient(135deg, #f857a6 0%, #ff9a6b 100%)',
    icon: <PeopleIcon />,
  },
  {
    key: 'subscribed',
    label: 'پیمانکاران مشترک',
    hint: 'دارای اشتراک',
    bg: 'linear-gradient(135deg, #06b875 0%, #4fd18b 100%)',
    icon: <CheckCircleIcon />,
  },
  {
    key: 'non-subscribed',
    label: 'پیمانکاران غیرمشترک',
    hint: 'بدون اشتراک',
    bg: 'linear-gradient(135deg, #f0455b 0%, #ff8a5b 100%)',
    icon: <CancelIcon />,
  },
]

/**
 * کارت‌های آمارِ داشبوردِ قراردادها — با کلیک، لیست فیلتر می‌شود.
 * mode: 'all' | 'subscribed' | 'non-subscribed'
 */
export default function StatsCards({ stats = {}, mode = 'all', onModeChange }) {
  const classes = useStyles()

  const valueOf = (key) => {
    if (key === 'all') return stats.contracts || 0
    if (key === 'contractors') return stats.contractors || 0
    if (key === 'subscribed') return stats.subscribed || 0
    return stats.nonSubscribed || 0
  }

  /**
   * الگوی یکسان برای هر چهار کارت:
   *   عددِ اول = پیمانکار   |   عددِ دوم = قرارداد   (هر دو با فونتِ برابر)
   */
  const pairOf = (key) => {
    if (key === 'all') return [stats.contracts || 0, 'قرارداد', stats.contractors || 0, 'پیمانکار']
    if (key === 'contractors') return [stats.contractors || 0, 'پیمانکار', stats.contracts || 0, 'قرارداد']
    if (key === 'subscribed') return [stats.subscribed || 0, 'پیمانکار مشترک', stats.subContracts || 0, 'قرارداد']
    return [stats.nonSubscribed || 0, 'پیمانکار غیرمشترک', stats.nonSubContracts || 0, 'قرارداد']
  }

  return (
    <Grid container spacing={2} style={{ marginBottom: 16 }} data-tour="stats-cards">
      {CARDS.map((it) => {
        const isActive =
          (it.key === 'all' && mode === 'all') ||
          (it.key === 'subscribed' && mode === 'subscribed') ||
          (it.key === 'non-subscribed' && mode === 'non-subscribed')
        return (
          <Grid item xs={6} md={3} key={it.key}>
            <Paper
              elevation={0}
              className={classes.card + (isActive ? ' ' + classes.active : '')}
              style={{ background: it.bg }}
              onClick={() => {
                if (!onModeChange) return
                if (it.key === 'all' || it.key === 'contractors') onModeChange('all')
                else onModeChange(it.key)
              }}
            >
              <span className={classes.blob} />
              <span className={classes.blob2} />
              <span className={classes.iconBox}>{it.icon}</span>
              <div>
                <Box display="flex" alignItems="baseline" style={{ gap: 6, flexWrap: 'wrap' }}>
                  <Typography className={classes.value}>{toFarsiDigits(pairOf(it.key)[0])}</Typography>
                  <Typography className={classes.unit}>{pairOf(it.key)[1]}</Typography>
                  <Typography style={{ opacity: .6, fontSize: 18, fontWeight: 900 }}>·</Typography>
                  <Typography className={classes.value2}>{toFarsiDigits(pairOf(it.key)[2])}</Typography>
                  <Typography className={classes.unit}>{pairOf(it.key)[3]}</Typography>
                </Box>
                <Typography className={classes.label}>{it.label}</Typography>
                <Typography className={classes.hint}>{it.hint}</Typography>
              </div>
            </Paper>
          </Grid>
        )
      })}
    </Grid>
  )
}
